// src/store/productStore.js

import { reactive, readonly, toRaw } from "vue"; // Import toRaw
import { productService } from '@/services/product-viewer/productService';

// ===================================================================
// 1. HYDRATION: Load state from localStorage on initial load
// ===================================================================
const loadFromCache = () => {
  const cachedState = localStorage.getItem("productStoreCache");
  if (cachedState) {
    try {
      return JSON.parse(cachedState);
    } catch (e) {
      console.error("Failed to parse product store cache", e);
      return {};
    }
  }
  return {};
};

const cachedData = loadFromCache();

// Default empty state for filter options
const defaultTopLevelFilterOptions = {
  brandId: [],
  webCategory: [],
  materialName: [],
  variantColor: [],
  backendCategory: [],
};

const state = reactive({
  // Catalog data
  categoryTree: cachedData.categoryTree || {},
  backendCategoryTree: cachedData.backendCategoryTree || [],
  catalogProducts: [],
  catalogTotal: 0,

  primaryFilterKeys: [
    "brandId",
    "webCategory",
    "materialName",
    "backendCategory",
    "alliumInternalData.processingDetails.gpcBrickUnchanged",
  ],
  topLevelFilterOptions: cachedData.topLevelFilterOptions || defaultTopLevelFilterOptions,
  allAttributeOptions: cachedData.allAttributeOptions || [],
  taxonomyFilterOptions: cachedData.taxonomyFilterOptions || [],
  catalogFilters: {
    brandSearch: "",
    alliumSearch: "",
    webCategorySearch: "",
    selectedFilters: {
      brandId: [],
      webCategory: [],
      materialName: [],
      variantColor: [],
      backendCategory: [],
      "alliumInternalData.processingDetails.gpcBrickUnchanged": [],
    },
    selectedCategoryPath: [],
    selectedAttrId: "",
    selectedAttrValue: "",
    attrExists: null,
    currentPage: 1,
    pageSize: 20,
    showAttributeFlags: false,
  },
  isLoading: false,
  hasMoreProducts: true,
  isFetchingMore: false,
  selectedDatabase: "test",
  products: {},
  allProductIds: [],
  filteredProductIds: [],
  currentProductIndex: null,
  coverageData: [],
  coverageMetadata: {},
  coverageLoading: false,
  coverageError: "",
  user: null,
  isAuthenticated: false,
  isAdmin: false,
  isWrite: false,
  isAuthReady: false,
  lastCategoryTreeFetch: cachedData.lastCategoryTreeFetch || null,
  lastInitDataFetch: cachedData.lastInitDataFetch || null,
  lastAttributeOptionsFetch: cachedData.lastAttributeOptionsFetch || null,
  lastTopLevelCountsFetch: cachedData.lastTopLevelCountsFetch || null,
  lastTaxonomyFilterOptionsFetch: cachedData.lastTaxonomyFilterOptionsFetch || null,
});

// ===================================================================
// 2. PERSISTENCE: Helper function to explicitly save state to localStorage
// ===================================================================
const saveCache = () => {
  try {
    const stateToCache = {
      lastCategoryTreeFetch: state.lastCategoryTreeFetch,
      lastInitDataFetch: state.lastInitDataFetch,
      lastAttributeOptionsFetch: state.lastAttributeOptionsFetch,
      lastTopLevelCountsFetch: state.lastTopLevelCountsFetch,
      lastTaxonomyFilterOptionsFetch: state.lastTaxonomyFilterOptionsFetch,
      categoryTree: toRaw(state.categoryTree),
      backendCategoryTree: toRaw(state.backendCategoryTree),
      allAttributeOptions: toRaw(state.allAttributeOptions),
      topLevelFilterOptions: toRaw(state.topLevelFilterOptions),
      taxonomyFilterOptions: toRaw(state.taxonomyFilterOptions),
    };
    localStorage.setItem("productStoreCache", JSON.stringify(stateToCache));
    console.log("Cache saved to localStorage.", toRaw(state.categoryTree), state.categoryTree);
  } catch (e) {
    console.error("Failed to save cache to localStorage", e);
  }
};

const actions = {
  resetCatalogState() {
    state.catalogProducts = [];
    state.catalogTotal = 0;
    state.allAttributeOptions = [];
    state.topLevelFilterOptions = {
      brandId: [],
      webCategory: [],
      materialName: [],
      variantColor: [],
      backendCategory: [],
    };
    state.catalogFilters = {
      ...state.catalogFilters,
      brandSearch: "",
      alliumSearch: "",
      webCategorySearch: "",
      selectedFilters: {
        brandId: [],
        webCategory: [],
        materialName: [],
        variantColor: [],
        backendCategory: [],
        "alliumInternalData.processingDetails.gpcBrickUnchanged": [],
      },
      selectedCategoryPath: [],
      selectedAttrId: "",
      selectedAttrValue: "",
      currentPage: 1,
    };
    state.hasMoreProducts = true;
  },

  // ---------------------------------------
  // Catalog / Product
  // ---------------------------------------
  async initCatalogData() {
    state.isLoading = true;
    try {
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;

      const isCacheFresh =
        state.lastInitDataFetch &&
        now - state.lastInitDataFetch < fiveMinutes &&
        Object.keys(state.categoryTree).length > 0;

      const fetchPromises = [];
      fetchPromises.push(actions.fetchCatalogProducts());

      if (!isCacheFresh) {
        console.log(
          "CACHE MISS: Fetching static catalog data (tree, counts, attributes)..."
        );
        fetchPromises.push(actions.fetchTopLevelCounts());
        fetchPromises.push(actions.fetchAttributeOptions());
        fetchPromises.push(actions.fetchTaxonomyFilterOptions());
        fetchPromises.push(actions.fetchCategoryTree());
      } else {
        console.log(
          "CACHE HIT: Skipping static data fetch. Products will still be fetched."
        );
      }

      await Promise.all(fetchPromises);

      if (!isCacheFresh) {
        state.lastInitDataFetch = Date.now();
        saveCache();
      }
    } catch (err) {
      console.error("initCatalogData error:", err);
    } finally {
      state.isLoading = false;
    }
  },

  async fetchCategoryTree() {
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;

    const isCacheFresh =
      state.lastCategoryTreeFetch &&
      now - state.lastCategoryTreeFetch < fiveMinutes &&
      Object.keys(state.categoryTree).length > 0;

    if (isCacheFresh) {
      console.log(
        "CACHE HIT: Skipping fetchCategoryTree as it is individually fresh."
      );
      return;
    }

    console.log("CACHE MISS: Fetching category tree...");
    try {
      // TODO: uncomment for category tree
      // const chains = await productService.fetchCategoryTree(
      //   state.selectedDatabase
      // );
      // const tree = buildCategoryTree(chains);
      // state.categoryTree = tree;
      // state.lastCategoryTreeFetch = Date.now();
      // saveCache();
    } catch (err) {
      console.error("fetchCategoryTree error:", err);
    }
  },

  async setSelectedDatabase(newDb) {
    if (state.selectedDatabase === newDb) return;
    state.isLoading = true;
    state.selectedDatabase = newDb;

    state.lastInitDataFetch = null;
    state.lastCategoryTreeFetch = null;
    state.lastAttributeOptionsFetch = null;
    state.lastTopLevelCountsFetch = null; // Invalidate cache on DB change
    saveCache();

    actions.resetCatalogState();
    try {
      await actions.initCatalogData();
    } catch (err) {
      console.error(`Failed to load data for database: ${newDb}`, err);
    } finally {
      state.isLoading = false;
    }
  },

  // ... other actions remain the same

  async fetchTopLevelCounts() {
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;

    // Check if data exists in any of the primary filter option arrays
    const hasData = Object.values(state.topLevelFilterOptions).some(arr => arr.length > 0);

    const isCacheFresh =
      state.lastTopLevelCountsFetch &&
      now - state.lastTopLevelCountsFetch < fiveMinutes &&
      hasData;

    if (isCacheFresh) {
      console.log(
        "CACHE HIT: Skipping fetchTopLevelCounts as it is individually fresh."
      );
      return;
    }
    console.log("CACHE MISS: Fetching top level counts...");

    try {
      // Fetch regular top level counts
      const data = await productService.fetchTopLevelCounts(
        state.selectedDatabase
      );
      const topData = data.data || {};
      const newOptions = { ...defaultTopLevelFilterOptions };

      for (const key in topData) {
        if (
          Object.prototype.hasOwnProperty.call(newOptions, key) &&
          Array.isArray(topData[key])
        ) {
          newOptions[key] = [...topData[key]].sort(
            (a, b) => b.count - a.count
          );
        }
      }

      // Fetch backend category taxonomy and build the list with counts
      try {
        const backendCategories = await productService.fetchBackendCategoryTaxonomy(state.selectedDatabase);
        state.backendCategoryTree = backendCategories;
        
        // For backward compatibility, also create a flat list for topLevelFilterOptions
        // This will be populated with counts from the API
        newOptions.backendCategory = [];
      } catch (backendErr) {
        console.error("Error fetching backend categories:", backendErr);
        state.backendCategoryTree = [];
        newOptions.backendCategory = [];
      }

      state.topLevelFilterOptions = newOptions;

      state.lastTopLevelCountsFetch = Date.now();
      saveCache();
    } catch (err) {
      console.error("Error fetching top level counts:", err);
    }
  },
  async fetchTaxonomyFilterOptions() {
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;

    const isCacheFresh =
      state.lastTaxonomyFilterOptionsFetch &&
      now - state.lastTaxonomyFilterOptionsFetch < fiveMinutes &&
      state.taxonomyFilterOptions.length > 0;

    if (isCacheFresh) {
      console.log(
        "CACHE HIT: Skipping fetchTaxonomyFilterOptions as it is individually fresh."
      );
      return;
    }
    console.log("CACHE MISS: Fetching taxonomies...");

    try {
      // Fetch regular top level counts
      const data = await productService.fetchTaxonomyFilterData(
        state.selectedDatabase
      );
      state.taxonomyFilterOptions = data;
      state.lastTaxonomyFilterOptionsFetch = Date.now();
    } catch (err) {
      console.error("Error fetching taxonomies:", err);
    }
  },
  async fetchAttributeOptions() {
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;

    const isCacheFresh =
      state.lastAttributeOptionsFetch &&
      now - state.lastAttributeOptionsFetch < fiveMinutes &&
      state.allAttributeOptions.length > 0;

    if (isCacheFresh) {
      console.log(
        "CACHE HIT: Skipping fetchAttributeOptions as it is individually fresh."
      );
      return;
    }
    console.log("CACHE MISS: Fetching attribute options...");

    try {
      const data = await productService.fetchAttributeOptions(
        state.selectedDatabase
      );
      const attrCounts = data.data.attributeCounts || [];
      const valCounts = data.data.valueCounts || [];
      const originalArr = data.data.original || [];
      const attrMap = {};
      for (const a of attrCounts) {
        attrMap[a.attrId] = {
          attrId: a.attrId,
          displayName: `Attr ${a.attrId}`,
          totalCount: a.docCount,
          values: [],
        };
      }
      const nameMap = {};
      for (const obj of originalArr) {
        if (obj._id && obj.allAttrNames && obj.allAttrNames.length > 0) {
          nameMap[obj._id] = obj.allAttrNames[0];
        }
      }
      for (const [attrId, info] of Object.entries(attrMap)) {
        if (nameMap[attrId]) {
          info.displayName = nameMap[attrId];
        }
      }
      for (const v of valCounts) {
        if (attrMap[v.attrId]) {
          attrMap[v.attrId].values.push({
            valueCode: v.valueCode,
            displayValue: v.displayValue,
            count: v.docCount,
          });
        }
      }
      for (const info of Object.values(attrMap)) {
        info.values.sort((x, y) => y.count - x.count);
      }
      const arr = Object.values(attrMap).sort(
        (a, b) => b.totalCount - a.totalCount
      );
      state.allAttributeOptions = arr;
      state.lastAttributeOptionsFetch = Date.now();
      saveCache();
    } catch (err) {
      console.error("Error fetching attribute options:", err);
    }
  },

  async fetchCatalogProducts() {
    state.isLoading = true;
    try {
      const payload = {
        ...state.catalogFilters,
        dbName: state.selectedDatabase,
      };
      const response = await productService.fetchCatalogProducts(payload);
      state.catalogTotal = response.total || 0;
      const rawProds = response.products || [];
      state.catalogProducts = rawProds.map((p) => {
        state.products[p.id] = p;
        if (!state.allProductIds.includes(p.id)) {
          state.allProductIds.push(p.id);
        }
        let categoryStr = "";
        const webLevelsArr = p.categoryParentLinesMap?.Web || [];
        if (Array.isArray(webLevelsArr[0])) {
          const chain = webLevelsArr[0];
          categoryStr = chain
            .map((level) => level.title?.EN || level.id)
            .join(" / ");
        }
        const fallbackImage =
          p.imageURLs?.[0] ||
          p.variants?.[0]?.imageURLs?.[0] ||
          "https://placehold.co/200x200?text=No+Image";
        const displayTitle =
          p.title?.EN ||
          p.title?.DE ||
          p.title?.FR ||
          p.title?.IT ||
          "No title";
        return {
          ...p,
          title: displayTitle,
          brand: p.brandId || "",
          category: categoryStr,
          image: fallbackImage,
        };
      });
      state.filteredProductIds = state.catalogProducts.map((item) => item.id);
      state.hasMoreProducts = rawProds.length === state.catalogFilters.pageSize;
    } catch (err) {
      console.error("Error fetching catalog products:", err);
    } finally {
      state.isLoading = false;
    }
  },

  updateCatalogFilters(newFilters) {
    const updatedFilters = { ...state.catalogFilters, ...newFilters };
    if (!newFilters.hasOwnProperty("currentPage")) {
      updatedFilters.currentPage = 1;
    }
    state.catalogFilters = updatedFilters;
    actions.fetchFilteredData();
  },

  async fetchFilteredData() {
    state.isLoading = true;
    try {
      const filterPayload = {
        ...state.catalogFilters,
        dbName: state.selectedDatabase,
      };
      const response = await productService.fetchCatalogProducts(filterPayload);
      state.catalogTotal = response.total || 0;
      const rawProds = response.products || [];
      state.catalogProducts = rawProds.map((p) => {
        state.products[p.id] = p;
        if (!state.allProductIds.includes(p.id)) {
          state.allProductIds.push(p.id);
        }
        let categoryStr = "";
        const webLevelsArr = p.categoryParentLinesMap?.Web || [];
        if (Array.isArray(webLevelsArr[0])) {
          const chain = webLevelsArr[0];
          categoryStr = chain
            .map((level) => level.title?.EN || level.id)
            .join(" / ");
        }
        const fallbackImage =
          p.imageURLs?.[0] ||
          p.variants?.[0]?.imageURLs?.[0] ||
          "https://placehold.co/200x200?text=No+Image";
        const displayTitle =
          p.title?.EN ||
          p.title?.DE ||
          p.title?.FR ||
          p.title?.IT ||
          "No title";
        return {
          ...p,
          title: displayTitle,
          brand: p.brandId || "",
          category: categoryStr,
          image: fallbackImage,
        };
      });
      state.filteredProductIds = state.catalogProducts.map((p) => p.id);
      state.hasMoreProducts = rawProds.length === state.catalogFilters.pageSize;
      const optionsData = response.options || {};
      for (const key in state.topLevelFilterOptions) {
        if (optionsData[key]) {
          state.topLevelFilterOptions[key] = optionsData[key];
        }
      }
    } catch (err) {
      console.error("Error fetching filtered data:", err);
    } finally {
      state.isLoading = false;
    }
  },

  async loadProduct(productId) {
    state.isLoading = true;
    try {
      const product = await productService.fetchProcessedProductById(
        productId,
        state.selectedDatabase
      );
      if (!product) {
        delete state.products[productId];
        const idx1 = state.allProductIds.indexOf(productId);
        if (idx1 >= 0) state.allProductIds.splice(idx1, 1);
        const idx2 = state.filteredProductIds.indexOf(productId);
        if (idx2 >= 0) state.filteredProductIds.splice(idx2, 1);
        return null;
      }
      state.products[productId] = product;
      if (!state.allProductIds.includes(productId)) {
        state.allProductIds.push(productId);
      }
      if (!state.filteredProductIds.includes(productId)) {
        state.filteredProductIds.push(productId);
      }
      return product;
    } catch (err) {
      console.error("Error loading product:", err);
      return null;
    } finally {
      state.isLoading = false;
    }
  },

  async saveProduct(product, userEmail) {
    state.isLoading = true;
    try {
      if (!product.dbName) {
        product.dbName = state.selectedDatabase;
      }
      const result = await productService.saveProduct(product, userEmail);
      if (product.id) {
        state.products[product.id] = product;
      }
      return result;
    } catch (err) {
      console.error("Error saving product:", err);
      throw err;
    } finally {
      state.isLoading = false;
    }
  },

  setAuthState(user, isAdmin, isWrite) {
    state.user = user;
    state.isAuthenticated = !!user;
    state.isAdmin = isAdmin;
    state.isWrite = isWrite;
    state.isAuthReady = true;
  },

  async preLoadNextBatch() {
    if (state.isFetchingMore || !state.hasMoreProducts) return;
    state.isFetchingMore = true;
    try {
      const nextPagePayload = {
        ...state.catalogFilters,
        currentPage: state.catalogFilters.currentPage + 1,
        dbName: state.selectedDatabase,
      };
      const response = await productService.fetchCatalogProducts(
        nextPagePayload
      );
      const newProds = response.products || [];
      if (newProds.length > 0) {
        const newItems = newProds.map((p) => {
          state.products[p.id] = p;
          if (!state.allProductIds.includes(p.id))
            state.allProductIds.push(p.id);
          if (!state.filteredProductIds.includes(p.id))
            state.filteredProductIds.push(p.id);
          const displayTitle = p.title?.EN || p.title?.DE || "No title";
          let categoryStr = "";
          const webLevelsArr = p.categoryParentLinesMap?.Web?.[0];
          if (Array.isArray(webLevelsArr)) {
            categoryStr = webLevelsArr
              .map((level) => level.title?.EN || level.id)
              .join(" / ");
          }
          const imageUrl =
            p.imageURLs?.[0] ||
            p.variants?.[0]?.imageURLs?.[0] ||
            "https://placehold.co/200x200?text=No+Image";
          return {
            id: p.id,
            title: displayTitle,
            brand: p.brandId || "",
            category: categoryStr,
            image: imageUrl,
          };
        });
        state.catalogProducts = [...state.catalogProducts, ...newItems];
        state.catalogFilters.currentPage += 1;
      }
      state.hasMoreProducts = newProds.length === state.catalogFilters.pageSize;
    } catch (err) {
      console.error("Error auto-lazy-loading more products:", err);
    } finally {
      state.isFetchingMore = false;
    }
  },

  addProductToState(product) {
    if (!product || !product.id) return null;
    state.products[product.id] = product;
    if (!state.allProductIds.includes(product.id))
      state.allProductIds.push(product.id);
    if (!state.filteredProductIds.includes(product.id))
      state.filteredProductIds.push(product.id);
    const displayTitle = product.title?.EN || product.title?.DE || "No title";
    let categoryStr = "";
    const webLevelsArr = product.categoryParentLinesMap?.Web?.[0];
    if (Array.isArray(webLevelsArr)) {
      categoryStr = webLevelsArr
        .map((level) => level.title?.EN || level.id)
        .join(" / ");
    }
    const catalogItem = {
      id: product.id,
      title: displayTitle,
      brand: product.brandId || "",
      category: categoryStr,
      image:
        product.imageURLs?.[0] ||
        product.variants?.[0]?.imageURLs?.[0] ||
        "https://placehold.co/200x200?text=No+Image",
    };
    if (!state.catalogProducts.some((p) => p.id === product.id)) {
      state.catalogProducts.push(catalogItem);
    }
    return product;
  },

  setIsLoading(value) {
    state.isLoading = value;
  },

  async fetchAttributeCoverage({ dbName, removeMissingImages = true }) {
    try {
      state.coverageLoading = true;
      state.coverageError = "";
      state.coverageData = [];
      state.coverageMetadata = {};
      const effectiveDbName = dbName || state.selectedDatabase;
      const resp = await productService.fetchCoverage(
        effectiveDbName,
        removeMissingImages
      );
      if (resp.coverage) state.coverageData = resp.coverage;
      if (resp.metadata) state.coverageMetadata = resp.metadata;
    } catch (err) {
      console.error("Error fetching coverage:", err);
      state.coverageError = err.message || String(err);
    } finally {
      state.coverageLoading = false;
    }
  },

  async reprocessProducts(payload) {
    const { partialProcessingSteps } = payload;
    try {
      const servicePayload = {
        ...state.catalogFilters,
        partialProcessingSteps,
        dbName: state.selectedDatabase,
        selectedIds: payload.selectedIds,
        userEmail: payload.userEmail,
      };
      const result = await productService.reprocessProductsByFilter(
        servicePayload
      );
      return result;
    } catch (error) {
      console.error("Error in reprocessProducts action:", error);
      throw error;
    }
  },
};

function buildCategoryTree(chains) {
  const root = {};
  (chains || []).forEach((chain) => {
    if (!Array.isArray(chain)) return;
    const reversed = chain.slice().reverse();
    let current = root;
    reversed.forEach((levelObj) => {
      if (!levelObj || !levelObj.id) return;
      const catId = levelObj.id;
      if (!current[catId]) {
        current[catId] = { __count: 0, __children: {} };
      }
      current[catId].__count += 1;
      current = current[catId].__children;
    });
  });
  return root;
}

const getters = {
  getCategoryTree: () => state.categoryTree,
  getBackendCategoryTree: () => state.backendCategoryTree,
  getCatalogProducts: () => state.catalogProducts,
  getCatalogTotal: () => state.catalogTotal,
  getIsLoading: () => state.isLoading,
  getAllAttributeOptions: () => state.allAttributeOptions,
  getTaxonomyFilterOptions: () => state.taxonomyFilterOptions,
  getTopLevelFilterOptions: () => state.topLevelFilterOptions,
  getCatalogFilters: () => state.catalogFilters,
  getPrimaryFilterKeys: () => state.primaryFilterKeys,
  getProduct: (id) => state.products[id] || null,
  getIsAuthenticated: () => state.isAuthenticated,
  getIsAdmin: () => state.isAdmin,
  getIsWrite: () => state.isWrite,
  getUser: () => state.user,
  getIsAuthReady: () => state.isAuthReady,
  getCoverageData: () => state.coverageData,
  getCoverageMetadata: () => state.coverageMetadata,
  getCoverageError: () => state.coverageError,
  getIsCoverageLoading: () => state.coverageLoading,
};

export const useProductStore = () => {
  return {
    state: readonly(state),
    ...getters,
    ...actions,
  };
};